/* eslint-disable no-console */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  OpenInNew,
} from '@mui/icons-material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { ReactComponent as EyeIcon } from '../../../assets/SVG/eye.svg';
import { ReactComponent as TrashIcon } from '../../../assets/SVG/trash.svg';

// File Type Icons
import { ReactComponent as PdfFileIcon } from '../../../assets/SVG/PDF.svg';
import { ReactComponent as DocxFileIcon } from '../../../assets/SVG/DOCX.svg';
import { ReactComponent as JpgFileIcon } from '../../../assets/SVG/JPG.svg';
import { ReactComponent as PngFileIcon } from '../../../assets/SVG/PNG.svg';
import { ReactComponent as DefaultFileIcon } from '../../../assets/SVG/pdfIcon.svg'; // Fallback

import { ImageItemProps } from './ImageUploadModal.types';
import { MAX_NAME_DISPLAY_LENGTH } from './ImageUploadModal.constants';
import { extractFilename } from './DocumentChips';
import EmbryoImageLightbox from '../../../pages/Embryology/Embryo_Culture/EmbryoImageLightbox';

/**
 * File Icon Component for ImageItem
 */
const FileIcon: React.FC<{ type: string; size?: number }> = ({ type, size = 48 }) => {
  const props = { width: size, height: size };
  
  switch (type) {
  case 'PDF':
    return <PdfFileIcon {...props} />;
  case 'DOCX':
    return <DocxFileIcon {...props} />;
  case 'JPG':
    return <JpgFileIcon {...props} />;
  case 'PNG':
    return <PngFileIcon {...props} />;
  default:
    return <DefaultFileIcon {...props} />;
  }
};

/**
 * ImageItem Component
 * Displays a single uploaded file with preview, name, size, and actions
 * Features: In-modal PDF preview, improved UI, file type badges
 */
const ImageItem: React.FC<ImageItemProps> = ({
  image,
  index,
  onRemove,
  viewMode = false,
}) => {
  const { t: tGeneral } = useTranslation('general');
  const { t: tSurgery } = useTranslation('surgery_config');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  // Clean filename - strip S3 query params if present
  const cleanName = extractFilename(image.name || image.image);
  const isPdf = cleanName.toLowerCase().endsWith('.pdf');
  
  // Helper to determine file type for icon selection
  const getFileType = () => {
    const filename = cleanName.toLowerCase();
    if (filename.endsWith('.pdf')) return 'PDF';
    if (filename.endsWith('.doc') || filename.endsWith('.docx')) return 'DOCX';
    if (filename.endsWith('.jpg') || filename.endsWith('.jpeg')) return 'JPG';
    if (filename.endsWith('.png')) return 'PNG';
    
    // Fallback checks
    if (image.image.startsWith('data:application/pdf')) return 'PDF';
    if (image.image.startsWith('data:image/jpeg')) return 'JPG';
    if (image.image.startsWith('data:image/png')) return 'PNG';

    return 'OTHER';
  };

  const fileType = getFileType();

  /**
   * Checks if the image is a URL (existing file from S3)
   */
  const isExternalUrl = (src: string): boolean => {
    return src.startsWith('http://') || src.startsWith('https://');
  };

  /**
   * Converts base64 to blob URL for PDF preview
   */
  const getPdfBlobUrl = (base64: string): string | null => {
    try {
      const base64Data = base64.split(',')[1];
      if (!base64Data) {
        console.error('Invalid base64 data');
        return null;
      }
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length)
        .fill(0)
        .map((_, i) => byteCharacters.charCodeAt(i));
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error creating PDF blob:', error);
      return null;
    }
  };

  /**
   * Opens PDF in modal preview
   */
  const openPdfPreview = () => {
    // If it's an external URL (S3), use it directly
    if (isExternalUrl(image.image)) {
      setPdfBlobUrl(image.image);
      setPdfPreviewOpen(true);
    } else {
      // It's base64, convert to blob URL
      const blobUrl = getPdfBlobUrl(image.image);
      if (blobUrl) {
        setPdfBlobUrl(blobUrl);
        setPdfPreviewOpen(true);
      }
    }
  };

  /**
   * Opens PDF in new tab
   */
  const openPdfInNewTab = () => {
    // If it's an external URL (S3), open directly
    if (isExternalUrl(image.image)) {
      window.open(image.image, '_blank');
    } else {
      // It's base64, convert to blob URL
      const blobUrl = getPdfBlobUrl(image.image);
      if (blobUrl) {
        window.open(blobUrl, '_blank');
      }
    }
  };

  /**
   * Closes PDF preview and cleans up blob URL (only if it's a blob URL)
   */
  const closePdfPreview = () => {
    setPdfPreviewOpen(false);
    // Only revoke if it's a blob URL (not an external URL)
    if (pdfBlobUrl && pdfBlobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(pdfBlobUrl);
    }
    setPdfBlobUrl(null);
  };

  /**
   * Truncates long file names with ellipsis
   */
  const truncateName = (name: string, maxLength: number): string =>
    name.length > maxLength ? name.slice(0, maxLength) + '...' : name;

  const handlePreview = () => {
    if (isPdf) {
      openPdfPreview();
    } else {
      setLightboxOpen(true);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#FFFFFF',
          borderRadius: 2,
          p: 1.5,
          border: '1px solid #E0E0E0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: '#FAFAFA',
            boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            borderColor: '#BDBDBD',
          },
        }}
      >
        {/* Thumbnail with file type indicator */}
        <Box
          sx={{
            position: 'relative',
            width: 48,
            height: 48,
            borderRadius: 1,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            mr: 1.5,
            flexShrink: 0,
            border: 'none', // Removed background/border for clean icon look
          }}
          onClick={handlePreview}
        >
          <FileIcon type={fileType} size={48} />
          
          {/* Upload complete indicator */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              backgroundColor: '#fff',
              borderRadius: '50%',
              display: 'flex',
            }}
          >
            <CheckCircle sx={{ fontSize: 14, color: '#4CAF50' }} />
          </Box>
        </Box>

        {/* File Info */}
        <Box sx={{ flexGrow: 1, minWidth: 0, mr: 1 }}>
          <Tooltip title={cleanName} placement="top">
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#333',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.25, // Reduced margin
                fontFamily: 'Nunito, sans-serif',
              }}
            >
              {truncateName(cleanName, MAX_NAME_DISPLAY_LENGTH)}
            </Typography>
          </Tooltip>
          
          {/* File size only (light grey) */}
          {image.size && (
            <Typography sx={{ fontSize: '12px', color: '#8F9BB3', fontFamily: 'Nunito, sans-serif' }}>
              {image.size} MB
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {/* View Button / Confirm Delete Button (swaps in place) */}
          <Box sx={{ position: 'relative', width: 32, height: 32 }}>
            {/* Eye Icon - Normal State */}
            <Tooltip title={isPdf ? tSurgery('viewPdf') : tSurgery('viewImage')}>
              <IconButton
                onClick={handlePreview}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  opacity: isConfirmingDelete ? 0 : 1,
                  transform: isConfirmingDelete ? 'scale(0.5) rotate(-90deg)' : 'scale(1) rotate(0deg)',
                  transition: 'opacity 0.2s ease, transform 0.2s ease',
                  pointerEvents: isConfirmingDelete ? 'none' : 'auto',
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                }}
              >
                <EyeIcon style={{ width: 20, height: 20 }} />
              </IconButton>
            </Tooltip>

            {/* Confirm Delete (Checkmark) - Confirmation State */}
            {!viewMode && (
              <Tooltip title={tGeneral('Confirm')}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setIsConfirmingDelete(false);
                    onRemove(index);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 32,
                    height: 32,
                    opacity: isConfirmingDelete ? 1 : 0,
                    transform: isConfirmingDelete ? 'scale(1) rotate(0deg)' : 'scale(0.5) rotate(90deg)',
                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                    pointerEvents: isConfirmingDelete ? 'auto' : 'none',
                    backgroundColor: '#FFEBEE',
                    border: '1px solid #FFCDD2',
                    '&:hover': { 
                      backgroundColor: '#FFCDD2',
                      transform: 'scale(1.08)',
                    },
                  }}
                >
                  <CheckCircle sx={{ fontSize: 18, color: '#D32F2F' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Delete Button / Cancel Button (swaps in place) */}
          {!viewMode && (
            <Box sx={{ position: 'relative', width: 32, height: 32 }}>
              {/* Trash Icon - Normal State */}
              <Tooltip title={tGeneral('Delete')}>
                <IconButton
                  size="small"
                  onClick={() => setIsConfirmingDelete(true)}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: isConfirmingDelete ? 0 : 1,
                    transform: isConfirmingDelete ? 'scale(0.5) rotate(90deg)' : 'scale(1) rotate(0deg)',
                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                    pointerEvents: isConfirmingDelete ? 'none' : 'auto',
                    '&:hover': { backgroundColor: 'rgba(229, 57, 53, 0.08)' },
                  }}
                >
                  <TrashIcon style={{ width: 20, height: 20 }} />
                </IconButton>
              </Tooltip>

              {/* Cancel (Cross) - Confirmation State */}
              <Tooltip title={tGeneral('Cancel')}>
                <IconButton
                  size="small"
                  onClick={() => setIsConfirmingDelete(false)}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 32,
                    height: 32,
                    opacity: isConfirmingDelete ? 1 : 0,
                    transform: isConfirmingDelete ? 'scale(1) rotate(0deg)' : 'scale(0.5) rotate(-90deg)',
                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                    pointerEvents: isConfirmingDelete ? 'auto' : 'none',
                    backgroundColor: '#F5F5F5',
                    border: '1px solid #E0E0E0',
                    '&:hover': { 
                      backgroundColor: '#EEEEEE',
                      transform: 'scale(1.08)',
                    },
                  }}
                >
                  <Close sx={{ fontSize: 18, color: '#757575' }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      {/* Image Lightbox */}
      {lightboxOpen && !isPdf && (
        <EmbryoImageLightbox
          imageUrl={image.image}
          embryoNumber={'#' + (index + 1)}
          onClose={() => setLightboxOpen(false)}
          label={cleanName}
        />
      )}

      {/* PDF Preview Dialog */}
      <Dialog
        open={pdfPreviewOpen}
        onClose={closePdfPreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #E0E0E0',
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PictureAsPdfIcon sx={{ color: '#E53935' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Nunito, sans-serif' }}>
              {cleanName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Open in new tab">
              <IconButton onClick={openPdfInNewTab} size="small">
                <OpenInNew />
              </IconButton>
            </Tooltip>
            <IconButton onClick={closePdfPreview} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(100% - 120px)' }}>
          {pdfBlobUrl && (
            <iframe
              src={pdfBlobUrl}
              title={cleanName}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #E0E0E0', py: 1.5 }}>
          <Button onClick={closePdfPreview} variant="outlined" sx={{ fontFamily: 'Nunito, sans-serif' }}>
            {tGeneral('close')}
          </Button>
          <Button
            onClick={openPdfInNewTab}
            variant="contained"
            startIcon={<OpenInNew />}
            sx={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Open in New Tab
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImageItem;
